import { Team } from '@yandex-int/k-common/typings';
import {
    Modal,
    showIntlMessageNotification as showIntlMessageNotificationAction,
    useGeneratePath,
    useStaticUrls,
} from 'platform-components/common';
import { GRAVITIES, NOTIFICATION_THEMES } from 'platform-components/constants';
import { CloseButton } from 'platform-components/src/components/close-button/_view/close-button_view_outer';
import { ModalCloseProps } from 'platform-components/src/components/modal/types';
import React from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { bindActionCreators, Dispatch } from 'redux';

import * as userActions from 'common.components/user/actions';
import { NOTIFICATION_CLOSE_DELAY } from 'common.components/user/class.constants';
import { getCurrentClass, getCurrentTeamPrevLevel, getClasses } from 'common.components/user/selectors';
import { getTeamIdFromPath } from 'utils/path';

import { ReduxTeacherStore, RootAction } from '../../typings';

import NewYearModalContentArchive from './__content/new-year-modal__content_archive';
// @ts-ignore
import NewYearModalContentRename from './__content/new-year-modal__content_rename';
import * as actions from './actions';
import { PAGES } from './new-year-modal.constants';
import { getIsModalOpened, getPage, getShouldArchiveClass } from './selectors';

import './new-year-modal.scss';

interface StoreProps {
    currentTeam: Team | null;
    isOpened: boolean;
    page: PAGES;
    shouldArchiveClass: boolean;
    prevLevel: number | null;
    teams: Team[];
}

interface DispatchProps {
    renameTeam: typeof actions.renameTeam;
    archiveTeam: typeof actions.archiveTeam;
    clearState: typeof actions.clearState;
    recoverClassRequest: typeof userActions.recoverClassRequest;
    showIntlMessageNotification: typeof showIntlMessageNotificationAction;
    actualizeClassRequest: typeof actions.actualizeClassRequest;
}

interface Props extends StoreProps, DispatchProps {}

export const NewYearModalComponent = ({
    currentTeam,
    renameTeam,
    archiveTeam,
    page,
    isOpened,
    shouldArchiveClass,
    showIntlMessageNotification,
    recoverClassRequest,
    actualizeClassRequest,
    clearState,
    prevLevel,
    teams,
}: Props) => {
    const history = useHistory();
    const location = useLocation();
    const generatePath = useGeneratePath();
    const { labLibraryMain } = useStaticUrls();

    const reopenPageWithNextClass = () => {
        const teamId = teams.find((team) => team.active && currentTeam?.id !== team.id)?.id;

        if (!teamId) {
            window.location.href = generatePath(labLibraryMain);
        }

        let replacedClass = location.pathname.replace(/(classes\/)(\d+)/, `$1${teamId}`);

        history.push(replacedClass);
    };

    const onAfterRename = () => {
        if (currentTeam) {
            clearState();
        }
    };

    const handleRename = () => {
        if (currentTeam) {
            renameTeam(onAfterRename);
        }
    };

    const onAfterActualize = () => {
        if (!shouldArchiveClass && currentTeam) {
            clearState();
        }
    };

    const handleActualize = () => {
        actualizeClassRequest(onAfterActualize);
    };

    const showArchiveNotification = () => {
        if (currentTeam && prevLevel) {
            const { name } = currentTeam;

            showIntlMessageNotification({
                closeDelay: NOTIFICATION_CLOSE_DELAY,
                gravity: GRAVITIES.BOTTOM,
                messageId: 'toast.class.archived',
                messageValues: { name, level: prevLevel },
                notificationAction: () => recoverClassRequest({ ...currentTeam, level: prevLevel }),
                notificationMessageId: 'toast.class.recover',
                withExplicitClose: true,
                theme: NOTIFICATION_THEMES.TOAST,
            });
        }
    };

    const onAfterArchive = () => {
        if (currentTeam && prevLevel) {
            showArchiveNotification();
            clearState();
            reopenPageWithNextClass();
        }
    };

    const handleArchive = () => {
        archiveTeam(onAfterArchive);
    };

    const isArchiveContent = shouldArchiveClass || page === PAGES.ARCHIVE;

    const handleClose = () => {
        if (isArchiveContent) {
            archiveTeam(showArchiveNotification);
        } else if (currentTeam) {
            renameTeam();
        }
    };

    return (
        <Modal
            //@ts-ignore - EDUCATION-37077: [react-update]: fixme typings: Хочет ReactNode вместo JSX.Element
            Close={(props: ModalCloseProps) => <CloseButton {...props} position={CloseButton.POSITION.ABSOLUTE} />}
            autoclosable={false}
            hasClose
            mix="new-year-modal"
            onRequestClose={handleClose}
            opened={isOpened}
            theme={Modal.THEMES.TEACHER}
            useScale={false}
        >
            {isArchiveContent ? (
                <NewYearModalContentArchive onActualize={handleActualize} onArchive={handleArchive} />
            ) : (
                <NewYearModalContentRename onArchive={handleArchive} onRename={handleRename} />
            )}
        </Modal>
    );
};

const mapStateToProps = (storeState: ReduxTeacherStore): StoreProps => {
    const classId = getTeamIdFromPath();
    const prevLevel = getCurrentTeamPrevLevel(storeState, { classId });

    return {
        currentTeam: getCurrentClass(storeState, { classId }),
        isOpened: getIsModalOpened(storeState, { classId }),
        page: getPage(storeState),
        shouldArchiveClass: getShouldArchiveClass(storeState, { classId }),
        prevLevel,
        teams: getClasses(storeState),
    };
};

const mapDispatchToProps = (dispatch: Dispatch<RootAction>) => {
    const { archiveTeam, renameTeam, clearState, actualizeClassRequest } = actions;
    const { recoverClassRequest } = userActions;

    return bindActionCreators(
        {
            archiveTeam,
            renameTeam,
            clearState,
            recoverClassRequest,
            actualizeClassRequest,
            showIntlMessageNotification: showIntlMessageNotificationAction,
        },
        dispatch
    );
};

export const NewYearModalWrapper = connect(mapStateToProps, mapDispatchToProps)(NewYearModalComponent);

// быстрофикс https://st.yandex-team.ru/EDUCATION-25911
// во время загрузки:
// - есть classId, показываем модалку
// - куда-то пропадает classId, падает цепочка селекторов(так как модалка показана)
// - опять появляется classId, все ОК
// TODO раскопать куда пропадает classId
export const NewYearModal = ({}) => {
    const classId = getTeamIdFromPath();

    if (!classId) {
        return null;
    }

    return <NewYearModalWrapper />;
};
